import {
  Box, Stack, Typography, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { RowList } from "./RowList.component";
import { Modal, Spinner } from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { LEVEL } from "../../consts";
import { useStartOrResumeQuery } from "../../queries";
import { useNavigate } from "../../services";
import { useAuthStore, useLanguageStore } from "../../stores";
import { getError } from "../../utils/error";

const isNotFoundCode = "core-005";

export function CalendarioObblighi() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const { state: routerState } = useLocation();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);

  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);

  const { id: idString } = useParams<{
    id: string
  }>();
  const idNumber = idString ? Number(idString) : NaN;
  const language = useLanguageStore(state => state.language)?.id;

  const {
    data: learningObject,
    error:learningObjectError,
    isLoading: learningObjectIsFetching,
    isError:learningObjectIsError
  } = useStartOrResumeQuery({
    language,
    learningObjectId: idNumber,
    learningObjectTypology: LEVEL.DA_URL_RES
  });

  const hasWrongType = useMemo(() => {
    if(learningObjectIsFetching || !learningObject?.learningObjectTypology) return false;
    return learningObject.learningObjectTypology !== LEVEL.DA_URL_RES;
  }, [learningObjectIsFetching, learningObject]);


  const isNotFound = getError(learningObjectError)?.[0]?.cause === isNotFoundCode;
  const isNotEnrolled = getError(learningObjectError)?.[0]?.cause === "STUDENT_NOT_ENROLLED";

  if(isNotFound || hasWrongType){
    navigate("/not-found");
  }

  if (isNotEnrolled){
    navigate("/not-found", { state: { "message": t("student.not.enrolled") } });
  }

  useEffect(() => {
    if(learningObjectError && !isNotFound && !showModal) setShowModal(true);
  }, [learningObjectError, isNotFound, showModal]);

  if(learningObjectIsFetching) return (
    <Box sx={ {
      paddingTop: "32px",
      paddingBottom: "32px"
    } }>
      <Spinner />
    </Box>
  );

  return (
    <>
      <Box sx={ { backgroundColor: theme.customColors.backgroundPrimary, minHeight: "100vh" } }>
        { /* back to per-te */ }
        <ButtonDigited
          aria-label={ t("back") }
          typology="secondary"
          buttonSize="md"
          sx={ {
            margin: "2rem"
          } }
          href={ routerState?.previous ?? "/" }
        >
          <>
            <ChevronLeft color={ theme.customColors.textPrimary } size={ 20 } />
            { t("back") }
          </>
        </ButtonDigited>

        <Box sx={ {
          maxWidth: "1200px",
          margin: "0 auto",
          padding: isMobile ? "32px 16px" : "48px 32px"
        } }>
          <Stack direction="column" alignItems="center" justifyContent="center" gap="32px">
            { /* Titolo della pagina */ }
            <Box sx={ { textAlign: "center" } }>
              <Typography
                fontSize={ isMobile ? 24 : 32 }
                fontWeight={ 700 }
                color={ theme.customColors.textPrimary }
                sx={ { mb: 2 } }
              >
                { t("calendario_obblighi.title", "Calendario Obblighi Formativi") }
              </Typography>
              <Typography
                fontSize={ 16 }
                color={ theme.customColors.textPrimary }
                sx={ { opacity: 0.7 } }
              >
                { t("calendario_obblighi.subtitle", "I prossimi impegni, suddivisi per destinatario") }
              </Typography>
            </Box>

            { /* body component */ }
            <RowList />

          </Stack>
        </Box>
      </Box>
      <Modal
        description={ getError(learningObjectError)?.[0]?.message ?? "" }
        open={ learningObjectIsError && showModal }
        title={ getError(learningObjectError)?.[0]?.name ?? "" }
        onClose={ ()=> {
          setShowModal(false);
          navigate(-1);
        } }
      />
    </>
  );
}

